import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Link, usePage, router } from "@inertiajs/react";
import {
  Home,
  X,
  Calendar,
  Settings,
  Menu,
  User,
  LogOut,
  ChevronDown,
  MessageCircle,
} from "lucide-react";
import { Toaster } from "sonner";

const navigation = [
  { name: "ダッシュボード", href: "/staffs/dashboard", icon: Home },
  { name: "面談一覧", href: "/staffs/interview_requests", icon: Calendar },
  { name: "チャット", href: "/staffs/chat/conversations", icon: MessageCircle },
  { name: "設定", href: "/staffs/settings", icon: Settings },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface StaffLayoutProps {
  children: React.ReactNode;
}

export function StaffLayout({ children }: StaffLayoutProps) {
  const { url, props } = usePage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const currentPath = url;
  const currentStaff = (props as any).staff || { name: "スタッフ" };

  const isActive = (path: string) => currentPath.startsWith(path);

  const handleLogout = () => {
    router.delete("/staffs/sign_out");
  };

  const navigationWithCurrent = navigation.map((item) => ({
    ...item,
    current: isActive(item.href),
  }));

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-900/80" />
        <div className="fixed inset-0 flex">
          <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="-m-2.5 p-2.5"
              >
                <span className="sr-only">Close sidebar</span>
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
                <Link href="/staffs">
                  <h1 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    Ukarte Staff
                  </h1>
                </Link>
              </div>
              <div className="border-b border-gray-100 bg-emerald-50 -mx-6 px-6 py-4 mb-2">
                <div className="text-sm font-medium text-gray-700">
                  {currentStaff.name} 様
                </div>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul className="-mx-2 space-y-1">
                      {navigationWithCurrent.map((item) => (
                        <li key={item.name}>
                          <Link
                            href={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={classNames(
                              item.current
                                ? "bg-emerald-50 text-emerald-600"
                                : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-600",
                              "group flex gap-x-3 rounded-md p-2 text-sm font-semibold"
                            )}
                          >
                            <item.icon
                              className={classNames(
                                item.current
                                  ? "text-emerald-600"
                                  : "text-gray-400 group-hover:text-emerald-600",
                                "h-6 w-6 shrink-0"
                              )}
                            />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-0 overflow-y-auto border-r border-gray-200 bg-white pb-4">
          <div className="flex h-16 shrink-0 items-center px-6">
            <Link href="/staffs">
              <h1 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Ukarte Staff
              </h1>
            </Link>
          </div>
          <div className="px-6 py-4 border-b border-t border-gray-100 bg-emerald-50 mb-2">
            <div className="text-sm font-medium text-gray-700">
              {currentStaff.name} 様
            </div>
          </div>
          <nav className="flex flex-1 flex-col px-6">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navigationWithCurrent.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        className={classNames(
                          item.current
                            ? "bg-emerald-50 text-emerald-600"
                            : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-600",
                          "group flex gap-x-3 rounded-md p-2 text-sm font-semibold"
                        )}
                      >
                        <item.icon
                          className={classNames(
                            item.current
                              ? "text-emerald-600"
                              : "text-gray-400 group-hover:text-emerald-600",
                            "h-6 w-6 shrink-0"
                          )}
                        />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Navigation header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" />
          </button>

          <div className="h-6 w-px bg-gray-200 lg:hidden" />

          <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="-m-1.5 flex items-center p-1.5"
                >
                  <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-emerald-600" />
                  </div>
                  <ChevronDown className="ml-2 h-5 w-5 text-gray-400 hidden lg:block" />
                </button>

                {profileMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <div className="absolute right-0 z-50 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-3 py-1 text-sm text-gray-900 hover:bg-gray-50"
                      >
                        <LogOut className="h-4 w-4" />
                        ログアウト
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main
          className={classNames(
            "flex-1 bg-gray-50 min-h-[calc(100vh-4rem)]",
            currentPath.startsWith("/staffs/chat") ? "" : "p-6"
          )}
        >
          {children}
        </main>
      </div>

      <Toaster position="top-right" richColors closeButton />
    </>
  );
}
